import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  FormHelperText,
  Divider
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Select from 'react-select';
import useNotification from '../../hooks/useNotification';
import { analyzeContentFields, generateContentFromFields } from '../../utils/contentFieldParser';
import { getFieldConfig, generateContent } from '../../utils/roleFieldsConfig';

const AddSectionModal = ({
  open,
  onClose,
  onSave,
  sectionType = 'section',
  existingSections = [],
  featureOptions = []
}) => {
  const { showNotification } = useNotification();
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(true);

  // Track if fields have been initialized to prevent re-initialization
  const [fieldsInitialized, setFieldsInitialized] = useState(false);

  // State for field validation rules (specific to field-validation type)
  const [validationRules, setValidationRules] = useState([
    { type: '', value: '', message: '' }
  ]);

  // State for feature-select field (adding new feature)
  const [isAddingNewFeature, setIsAddingNewFeature] = useState(false);
  const [newFeatureText, setNewFeatureText] = useState('');

  // Analyze existing content to determine fields
  useEffect(() => {
    if (open && !fieldsInitialized) {
      setIsAnalyzing(true);
      // Use setTimeout to show loading state
      setTimeout(() => {
        let analyzedFields;

        // For field-validation, use custom fields
        if (sectionType === 'field-validation') {
          analyzedFields = [
            { name: 'title', label: 'Field Name', type: 'text', required: true, placeholder: 'e.g., Email, Username, Password' },
            { name: 'description', label: 'Description', type: 'textarea', required: false, rows: 2, placeholder: 'Brief description of this field' }
          ];
        }
        // For technology, use custom fields
        else if (sectionType === 'technology') {
          analyzedFields = [
            { name: 'title', label: 'Technology Name', type: 'text', required: true, placeholder: 'e.g., React 18+ with TypeScript' },
            { name: 'description', label: 'Description', type: 'textarea', required: true, rows: 3, placeholder: 'Brief description of the technology' },
            { name: 'justification', label: 'Justification', type: 'textarea', required: false, rows: 4, placeholder: 'Why this technology is chosen' },
            { name: 'scalability', label: 'Scalability', type: 'text', required: false, placeholder: 'Scalability considerations' }
          ];
        }
        // For architecture, use custom fields with mermaid support
        else if (sectionType === 'architecture') {
          analyzedFields = [
            { name: 'title', label: 'Section Title', type: 'text', required: true, placeholder: 'e.g., Component Architecture, Database Schema' },
            { name: 'description', label: 'Description', type: 'textarea', required: true, rows: 4, placeholder: 'Describe this architecture section...' },
            { name: 'mermaidCode', label: 'Mermaid Diagram Code (Optional)', type: 'textarea', required: false, rows: 10, placeholder: 'graph TD\n    A[Client] --> B[API Gateway]\n    B --> C[Service]\n    C --> D[Database]', helperText: 'Add Mermaid diagram code to visualize this architecture section' }
          ];
        }
        // For api-guideline, use custom fields without category selection
        else if (sectionType === 'api-guideline') {
          analyzedFields = [
            {
              name: 'title',
              label: 'Guideline Title',
              type: 'text',
              required: true,
              placeholder: 'e.g., RESTful Resource Modeling, Authentication Strategy'
            },
            {
              name: 'description',
              label: 'Description',
              type: 'textarea',
              required: true,
              rows: 4,
              placeholder: 'Describe this API guideline in detail...'
            },
            {
              name: 'examples',
              label: 'Examples (Optional)',
              type: 'textarea',
              required: false,
              rows: 6,
              placeholder: 'Add code examples or use cases:\n\n```json\n{\n  "example": "value"\n}\n```',
              helperText: 'Add code examples, URLs, or implementation details'
            }
          ];
        }
        // For security-guideline, use custom fields
        else if (sectionType === 'security-guideline') {
          analyzedFields = [
            {
              name: 'title',
              label: 'Security Guideline Title',
              type: 'text',
              required: true,
              placeholder: 'e.g., SQL Injection Prevention, CSRF Protection'
            },
            {
              name: 'description',
              label: 'Description',
              type: 'textarea',
              required: true,
              rows: 6,
              placeholder: 'Describe this security guideline in detail...\n\nInclude:\n- What vulnerability it addresses\n- How to implement\n- Best practices'
            },
            {
              name: 'examples',
              label: 'Code Examples (Optional)',
              type: 'textarea',
              required: false,
              rows: 8,
              placeholder: 'Add code examples, configuration snippets, or mermaid diagrams:\n\n```javascript\n// Example implementation\n```\n\nOr add mermaid diagram:\n\n```mermaid\ngraph TD\n    A[User Request] --> B[Validate Input]\n```',
              helperText: 'Add code examples, configurations, or mermaid diagrams',
              isMonospace: true
            }
          ];
        }
        // For permission, use custom fields with JavaScript code block
        else if (sectionType === 'permission') {
          analyzedFields = [
            {
              name: 'title',
              label: 'Permission Section Title',
              type: 'text',
              required: true,
              placeholder: 'e.g., Role Definitions, Resource Permissions, API Security'
            },
            {
              name: 'description',
              label: 'Description',
              type: 'textarea',
              required: true,
              rows: 6,
              placeholder: 'Describe this permission section in detail...\n\nInclude:\n- Purpose and scope\n- Access control rules\n- Implementation details\n- Security considerations'
            },
            {
              name: 'jsCode',
              label: 'JavaScript Code (Optional)',
              type: 'textarea',
              required: false,
              rows: 12,
              placeholder: 'Add JavaScript implementation code:\n\n// Example: Role-based access control\nconst checkPermission = (user, resource, action) => {\n  const userRole = user.role;\n  const permissions = rolePermissions[userRole];\n  \n  return permissions[resource]?.includes(action);\n};\n\n// Usage\nif (checkPermission(currentUser, "posts", "edit")) {\n  // Allow edit\n}',
              helperText: 'Add JavaScript code for permission checks, middleware, or helper functions',
              isMonospace: true
            }
          ];
        }
        // Use roleFieldsConfig for userRoles, userStories, test sections, git-guideline, and pipeline-stage
        else if (sectionType === 'userRoles' || sectionType === 'userStories' || sectionType === 'test-section' || sectionType === 'unit-test' || sectionType === 'functional-test-section' || sectionType === 'automated-test-section' || sectionType === 'security-test-section' || sectionType === 'git-guideline' || sectionType === 'pipeline-stage') {
          analyzedFields = getFieldConfig(sectionType);
        } else {
          analyzedFields = analyzeContentFields(existingSections);
        }

        setFields(analyzedFields);

        // Initialize form data with default values
        const initialData = {};
        analyzedFields.forEach(field => {
          if (field.type === 'select' && field.options && field.options.length > 0) {
            // For priority fields with MoSCoW, default to "should-have"
            if (field.name.toLowerCase().includes('priority')) {
              const shouldHaveOption = field.options.find(opt => opt.value === 'should-have');
              initialData[field.name] = shouldHaveOption ? 'should-have' : field.options[1]?.value || field.options[0].value;
            } else if (field.name === 'language') {
              // For language field, default to "javascript"
              initialData[field.name] = 'javascript';
            } else {
              // Set middle option as default for other select fields
              const middleIndex = Math.floor(field.options.length / 2);
              initialData[field.name] = field.options[middleIndex].value;
            }
          } else {
            initialData[field.name] = '';
          }
        });
        setFormData(initialData);
        setIsAnalyzing(false);
        setFieldsInitialized(true);
      }, 300);
    }
  }, [open, existingSections, sectionType, fieldsInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!open) {
      setFieldsInitialized(false);
      setValidationRules([{ type: '', value: '', message: '' }]);
      setIsAddingNewFeature(false);
      setNewFeatureText('');
    }
  }, [open]);

  // Handlers for validation rules
  const handleAddValidationRule = () => {
    setValidationRules([...validationRules, { type: '', value: '', message: '' }]);
  };

  const handleRemoveValidationRule = (index) => {
    if (validationRules.length > 1) {
      const updated = validationRules.filter((_, i) => i !== index);
      setValidationRules(updated);
    }
  };

  const handleValidationRuleChange = (index, field, value) => {
    const updated = [...validationRules];
    updated[index][field] = value;
    setValidationRules(updated);
  };

  const handleChange = (fieldName, isReactSelect = false) => (eventOrValue) => {
    const value = isReactSelect ? eventOrValue?.value : eventOrValue.target.value;
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear error when user types
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const visibleFields = fields.filter(shouldShowField);
    visibleFields.forEach(field => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Validate field-validation specific requirements
    if (sectionType === 'field-validation') {
      const hasValidRule = validationRules.some(rule =>
        rule.type.trim() && rule.message.trim()
      );
      if (!hasValidRule) {
        showNotification({
          message: 'Please add at least one validation rule with type and error message',
          type: 'error'
        });
        return;
      }
    }

    setIsSaving(true);
    try {
      // Generate content from fields
      let content;
      let rawData = formData;

      if (sectionType === 'field-validation') {
        // For field validation, include validation rules in rawData
        const filteredRules = validationRules.filter(rule =>
          rule.type.trim() && rule.message.trim()
        ).map(rule => ({
          type: rule.type.trim(),
          value: rule.value.trim() || true,
          message: rule.message.trim()
        }));

        rawData = {
          ...formData,
          type: 'Text',
          group: 'General',
          validations: filteredRules,
          implementation: ''
        };
        content = formData.description || formData.title;
      } else if (sectionType === 'architecture') {
        // For architecture, combine description and mermaid code
        let contentParts = [];

        if (formData.description?.trim()) {
          contentParts.push(formData.description.trim());
        }

        if (formData.mermaidCode?.trim()) {
          contentParts.push('\n```mermaid\n' + formData.mermaidCode.trim() + '\n```');
        }

        content = contentParts.join('\n\n');
      } else if (sectionType === 'api-guideline') {
        // For api-guideline, combine description and examples
        let contentParts = [];

        if (formData.description?.trim()) {
          contentParts.push(formData.description.trim());
        }

        if (formData.examples?.trim()) {
          contentParts.push('\n**Examples:**\n\n' + formData.examples.trim());
        }

        content = contentParts.join('\n\n');
      } else if (sectionType === 'security-guideline') {
        // For security-guideline, combine description and examples
        let contentParts = [];

        if (formData.description?.trim()) {
          contentParts.push(formData.description.trim());
        }

        if (formData.examples?.trim()) {
          contentParts.push('\n' + formData.examples.trim());
        }

        content = contentParts.join('\n\n');
      } else if (sectionType === 'permission') {
        // For permission, combine description and JavaScript code
        let contentParts = [];

        if (formData.description?.trim()) {
          contentParts.push(formData.description.trim());
        }

        if (formData.jsCode?.trim()) {
          contentParts.push('\n**Implementation:**\n\n```javascript\n' + formData.jsCode.trim() + '\n```');
        }

        content = contentParts.join('\n\n');
      } else if (sectionType === 'userRoles' || sectionType === 'userStories' || sectionType === 'test-section' || sectionType === 'unit-test' || sectionType === 'functional-test-section' || sectionType === 'automated-test-section' || sectionType === 'security-test-section' || sectionType === 'git-guideline' || sectionType === 'pipeline-stage') {
        content = generateContent(sectionType, formData);
      } else {
        content = generateContentFromFields(formData, fields);
      }

      const newSection = {
        title: formData.title?.trim(),
        content: content,
        category: formData.category || undefined, // Include category for api-guideline
        rawData: rawData // Include raw data for potential future use
      };

      await onSave(newSection);

      // Reset form completely
      setFormData({});
      setErrors({});
      setFields([]);
      setFieldsInitialized(false);
      setIsAnalyzing(true);
      setValidationRules([{ type: '', value: '', message: '' }]);
      setIsAddingNewFeature(false);
      setNewFeatureText('');

      onClose();
    } catch (error) {
      console.error('Error saving section:', error);
      // Error notification handled by parent component
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setFormData({});
      setErrors({});
      setFields([]);
      setFieldsInitialized(false);
      setIsAnalyzing(true);
      setValidationRules([{ type: '', value: '', message: '' }]);
      setIsAddingNewFeature(false);
      setNewFeatureText('');
      onClose();
    }
  };

  // Common styles for text fields
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      color: 'var(--color-blue, #475969)',
      transition: 'all 0.2s',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#485869',
        borderWidth: '1px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#485869',
        borderWidth: '1px',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4759694d',
      border: '1px solid #4759694d',
      borderRadius: '4px',
    },
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 14px',
      color: 'var(--color-blue, #475969)',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#a2abb3',
      opacity: 0.7,
      fontSize: '14px',
    },
    '& .MuiFormHelperText-root': {
      marginLeft: '4px',
      fontSize: '12px'
    }
  };

  // Fixed height style for specific fields (title, actor, trigger)
  const fixedHeightTextFieldStyles = {
    ...textFieldStyles,
    '& .MuiOutlinedInput-root': {
      ...textFieldStyles['& .MuiOutlinedInput-root'],
      height: '45px',
    }
  };

  const renderField = (field) => {
    // Render label separately like in CreateProject
    const renderLabel = () => (
      <Typography
        variant="body1"
        sx={{
          mb: 1,
          fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
          fontWeight: 700,
          fontSize: '14px',
          color: '#475969'
        }}
      >
        {field.label} {field.required && <span style={{ color: '#F89939' }}>*</span>}
      </Typography>
    );

    switch (field.type) {
      case 'textarea':
        return (
          <Box>
            {renderLabel()}
            <TextField
              fullWidth
              placeholder={field.placeholder}
              value={formData[field.name] || ''}
              onChange={handleChange(field.name)}
              error={!!errors[field.name]}
              helperText={errors[field.name] || field.helperText}
              disabled={isSaving}
              required={field.required}
              multiline
              rows={field.rows || 3}
              variant="outlined"
              sx={{
                ...textFieldStyles,
                ...(field.isMonospace && {
                  '& .MuiInputBase-input': {
                    fontFamily: "'Courier New', Courier, monospace",
                    fontSize: '13px',
                    lineHeight: '1.5',
                    padding: '12px 14px',
                    color: 'var(--color-blue, #475969)',
                  }
                })
              }}
            />
          </Box>
        );

      case 'select':
        const selectedOption = field.options?.find(opt => opt.value === formData[field.name]) || null;
        return (
          <Box>
            {renderLabel()}
            <Select
              value={selectedOption}
              onChange={handleChange(field.name, true)}
              options={field.options || []}
              isDisabled={isSaving}
              placeholder={`Select ${field.label.toLowerCase()}...`}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minHeight: '44px',
                  height: '44px',
                  borderColor: state.isFocused ? '#485869' : errors[field.name] ? '#d32f2f' : '#4759694d',
                  borderWidth: '1px',
                  borderRadius: '4px',
                  boxShadow: 'none',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  '&:hover': {
                    borderColor: '#485869'
                  }
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  height: '44px',
                  padding: '0 14px',
                  display: 'flex',
                  alignItems: 'center'
                }),
                input: (provided) => ({
                  ...provided,
                  margin: '0',
                  padding: '0',
                  color: '#475969',
                  fontSize: '14px'
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: '#a2abb3',
                  fontSize: '14px'
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: '#475969',
                  fontSize: '14px'
                }),
                menu: (provided) => ({
                  ...provided,
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e0e0e0',
                  marginTop: '4px'
                }),
                menuList: (provided) => ({
                  ...provided,
                  padding: '4px'
                }),
                option: (provided, state) => ({
                  ...provided,
                  fontSize: '14px',
                  padding: '8px 12px',
                  backgroundColor: state.isSelected ? '#485869' : state.isFocused ? '#f5f5f5' : 'white',
                  color: state.isSelected ? 'white' : '#475969',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#485869' : '#f5f5f5'
                  }
                }),
                indicatorSeparator: () => ({
                  display: 'none'
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  color: '#475969',
                  padding: '8px',
                  '&:hover': {
                    color: '#485869'
                  }
                })
              }}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: '#485869',
                  primary25: '#f5f5f5',
                  primary50: '#e8e8e8',
                  neutral20: '#4759694d',
                  neutral30: '#485869'
                }
              })}
            />
            {errors[field.name] && (
              <FormHelperText error sx={{ marginLeft: '4px', fontSize: '12px', marginTop: '4px' }}>
                {errors[field.name]}
              </FormHelperText>
            )}
          </Box>
        );

      case 'feature-select':
        // Convert featureOptions to react-select format
        const featureSelectOptions = featureOptions.map((feature) => {
          // Handle different feature formats - feature could be a parsed section object or a string
          const featureTitle = feature.title || feature.name || (typeof feature === 'string' ? feature : '');
          // Use title as-is since it usually already contains the feature ID (e.g., "F001 - Feature Name")
          return {
            value: featureTitle,
            label: featureTitle
          };
        }).filter(opt => opt.value); // Filter out empty options
        const selectedFeatureOption = formData[field.name]
          ? { value: formData[field.name], label: formData[field.name] }
          : null;

        return (
          <Box>
            {renderLabel()}
            {isAddingNewFeature ? (
              // Show text field for adding new feature
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Enter new feature name (e.g., F007 - Automated Email Report)"
                  value={newFeatureText}
                  onChange={(e) => setNewFeatureText(e.target.value)}
                  disabled={isSaving}
                  variant="outlined"
                  autoFocus
                  size="small"
                  sx={{
                    ...textFieldStyles,
                    '& .MuiOutlinedInput-root': {
                      ...textFieldStyles['& .MuiOutlinedInput-root'],
                      height: '44px'
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '10px 14px',
                      height: '24px'
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (newFeatureText.trim()) {
                      setFormData(prev => ({
                        ...prev,
                        [field.name]: newFeatureText.trim()
                      }));
                      setIsAddingNewFeature(false);
                      setNewFeatureText('');
                    }
                  }}
                  disabled={!newFeatureText.trim()}
                  sx={{
                    minWidth: '80px',
                    height: '44px',
                    bgcolor: '#485869',
                    '&:hover': { bgcolor: '#3a4a59' },
                    textTransform: 'none'
                  }}
                >
                  Add
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsAddingNewFeature(false);
                    setNewFeatureText('');
                  }}
                  sx={{
                    minWidth: '80px',
                    height: '44px',
                    color: '#475969',
                    borderColor: '#4759694d',
                    '&:hover': { borderColor: '#475969' },
                    textTransform: 'none'
                  }}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              // Show dropdown with "Add new feature" button below
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Select
                  value={selectedFeatureOption}
                  onChange={(option) => {
                    setFormData(prev => ({
                      ...prev,
                      [field.name]: option?.value || ''
                    }));
                  }}
                  options={featureSelectOptions}
                  isClearable
                  isDisabled={isSaving}
                  menuPlacement="top"
                  placeholder={featureSelectOptions.length > 0 ? "Select a feature..." : "No features available"}
                  noOptionsMessage={() => "No features found"}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: '44px',
                      height: '44px',
                      borderColor: state.isFocused ? '#485869' : errors[field.name] ? '#d32f2f' : '#4759694d',
                      borderWidth: '1px',
                      borderRadius: '4px',
                      boxShadow: 'none',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      '&:hover': { borderColor: '#485869' }
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: '44px',
                      padding: '0 14px',
                      display: 'flex',
                      alignItems: 'center'
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: '0',
                      padding: '0',
                      color: '#475969',
                      fontSize: '14px'
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#a2abb3',
                      fontSize: '14px'
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: '#475969',
                      fontSize: '14px'
                    }),
                    menu: (provided) => ({
                      ...provided,
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e0e0e0',
                      marginBottom: '4px'
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      padding: '4px'
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      fontSize: '14px',
                      padding: '8px 12px',
                      backgroundColor: state.isSelected ? '#485869' : state.isFocused ? '#f5f5f5' : 'white',
                      color: state.isSelected ? 'white' : '#475969',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#485869' : '#f5f5f5'
                      }
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#475969',
                      padding: '8px',
                      '&:hover': { color: '#485869' }
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      color: '#475969',
                      padding: '8px',
                      '&:hover': { color: '#d32f2f' }
                    })
                  }}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: '#485869',
                      primary25: '#f5f5f5',
                      primary50: '#e8e8e8',
                      neutral20: '#4759694d',
                      neutral30: '#485869'
                    }
                  })}
                />
                <Box sx={
                  {
                    textAlign:'right'
                  }
                }>
                  Not an existing feature?  <Button
                    variant="contained"
                    onClick={() => setIsAddingNewFeature(true)}
                    disabled={isSaving}
                    sx={{
                      color: '#485869',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      padding: '6px 15px',
                      justifyContent: 'flex-start',
                      width: 'fit-content',
                      marginLeft: 'auto',
                      '&:hover': {
                        backgroundColor: 'rgba(71, 89, 105, 0.04)'
                      },
                      textTransform: 'none !important'
                    }}
                  >
                    Add new feature
                  </Button>
                </Box>
              </Box>
            )}
            {errors[field.name] && (
              <FormHelperText error sx={{ marginLeft: '4px', fontSize: '12px', marginTop: '4px' }}>
                {errors[field.name]}
              </FormHelperText>
            )}
          </Box>
        );

      default:
        // Use fixed height for title, actors, and trigger fields
        const useFixedHeight = ['title', 'actors', 'trigger'].includes(field.name);
        return (
          <Box>
            {renderLabel()}
            <TextField
              fullWidth
              placeholder={field.placeholder}
              value={formData[field.name] || ''}
              onChange={handleChange(field.name)}
              error={!!errors[field.name]}
              helperText={errors[field.name]}
              disabled={isSaving}
              required={field.required}
              variant="outlined"
              autoFocus={field.name === 'title'}
              sx={useFixedHeight ? fixedHeightTextFieldStyles : textFieldStyles}
            />
          </Box>
        );
    }
  };

  // Check if a field should be displayed based on showIf condition
  const shouldShowField = (field) => {
    if (!field.showIf) return true;

    const { field: dependentField, value: requiredValue } = field.showIf;
    return formData[dependentField] === requiredValue;
  };

  // Group fields for better layout
  const groupFields = () => {
    // Filter fields based on showIf conditions
    const visibleFields = fields.filter(shouldShowField);

    // For userStories, userRoles, test sections, git-guideline, and pipeline-stage, maintain original order without grouping
    if (sectionType === 'userStories' || sectionType === 'userRoles' || sectionType === 'test-section' || sectionType === 'unit-test' || sectionType === 'functional-test-section' || sectionType === 'automated-test-section' || sectionType === 'security-test-section' || sectionType === 'git-guideline' || sectionType === 'pipeline-stage') {
      return {
        primary: visibleFields,
        selects: [],
        textareas: []
      };
    }

    const groups = {
      primary: [],
      selects: [],
      textareas: []
    };

    visibleFields.forEach(field => {
      if (field.name === 'title' || field.name === 'description') {
        groups.primary.push(field);
      } else if (field.type === 'select') {
        groups.selects.push(field);
      } else if (field.type === 'textarea') {
        groups.textareas.push(field);
      } else {
        groups.primary.push(field);
      }
    });

    // Ensure Priority and Complexity are ordered together if both exist
    if (groups.selects.length >= 2) {
      const priorityIndex = groups.selects.findIndex(f => f.name.toLowerCase().includes('priority'));
      const complexityIndex = groups.selects.findIndex(f => f.name.toLowerCase().includes('complexity'));

      if (priorityIndex !== -1 && complexityIndex !== -1 && priorityIndex !== 0) {
        // Move priority to first position
        const priority = groups.selects.splice(priorityIndex, 1)[0];
        groups.selects.unshift(priority);
      }

      if (complexityIndex !== -1 && complexityIndex !== 1 && groups.selects.length > 1) {
        // Move complexity to second position
        const currentComplexityIndex = groups.selects.findIndex(f => f.name.toLowerCase().includes('complexity'));
        if (currentComplexityIndex > 1) {
          const complexity = groups.selects.splice(currentComplexityIndex, 1)[0];
          groups.selects.splice(1, 0, complexity);
        }
      }
    }

    return groups;
  };

  const fieldGroups = groupFields();

  // Convert camelCase to readable title
  const getReadableType = (type) => {
    if (type === 'userStories') return 'User Story';
    if (type === 'userRoles') return 'User Role';
    if (type === 'field-validation') return 'Field Validation';
    if (type === 'security-guideline') return 'Security Guideline';
    if (type === 'api-guideline') return 'API Guideline';
    if (type === 'git-guideline') return 'Git Guideline';
    if (type === 'permission') return 'Permission Section';
    if (type === 'test-section') return 'Test Section';
    if (type === 'unit-test') return 'Test Section';
    if (type === 'functional-test-section') return 'Functional Test Section';
    if (type === 'automated-test-section') return 'Automated Test Section';
    if (type === 'security-test-section') return 'Security Test Section';
    if (type === 'pipeline-stage') return 'Pipeline Stage';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const capitalizedType = getReadableType(sectionType);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        pb: '14px',
        pt: '14px',
        pl: 5,
        pr: '10px',
        background: 'white'
      }}>
        <Box
          component="span"
          sx={{
            color: '#485869',
            fontSize: '1.1rem',
            fontWeight: 600,
            fontFamily: 'inherit'
          }}
        >
          Add New {capitalizedType}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
        mt: 4,
        py: 5,
        px: 5,
        bgcolor: 'white',
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
      }}>
        {isAnalyzing ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            p: 6
          }}>
            <CircularProgress
              size={48}
              sx={{
                color: '#485869',
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: '#475969',
                fontWeight: 500,
                fontSize: '16px',
                mb: 1
              }}
            >
              Analyzing structure...
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                fontSize: '14px'
              }}
            >
              This may take a few seconds
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            backgroundColor: '#ffffff',
            flex: 1,
            overflow: 'auto'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5
            }}>
              {/* Primary fields (title, description) */}
              {fieldGroups.primary.map(field => (
                <React.Fragment key={field.name}>
                  {renderField(field)}
                </React.Fragment>
              ))}

              {/* Select fields in a row if there are multiple */}
              {fieldGroups.selects.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: fieldGroups.selects.length > 3 ? 'column' : 'row'
                }}>
                  {fieldGroups.selects.map(field => (
                    <Box
                      key={field.name}
                      sx={{
                        flex: fieldGroups.selects.length === 2 ? '1 1 50%' : 1,
                        minWidth: 0 // Prevents flex items from overflowing
                      }}
                    >
                      {renderField(field)}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Textarea fields */}
              {fieldGroups.textareas.map(field => (
                <React.Fragment key={field.name}>
                  {renderField(field)}
                </React.Fragment>
              ))}

              {/* Validation Rules Section (only for field-validation type) */}
              {sectionType === 'field-validation' && (
                <Box sx={{ mt: 1 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#475969'
                    }}
                  >
                    Validation Rules <span style={{ color: '#F89939' }}>*</span>
                  </Typography>

                  {/* Column Headers */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 1,
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ flex: '0 0 25%' }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, fontSize: '12px' }}>
                        Type
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 20%' }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, fontSize: '12px' }}>
                        Value
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, fontSize: '12px' }}>
                        Error Message
                      </Typography>
                    </Box>
                    <Box sx={{ width: 40 }}></Box>
                  </Box>

                  {validationRules.map((rule, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 2,
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box sx={{ flex: '0 0 25%' }}>
                        <TextField
                          fullWidth
                          placeholder="e.g., required, minLength"
                          value={rule.type}
                          onChange={(e) => handleValidationRuleChange(index, 'type', e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={textFieldStyles}
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 20%' }}>
                        <TextField
                          fullWidth
                          placeholder="Value (optional)"
                          value={rule.value}
                          onChange={(e) => handleValidationRuleChange(index, 'value', e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={textFieldStyles}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          placeholder="Error message"
                          value={rule.message}
                          onChange={(e) => handleValidationRuleChange(index, 'message', e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={textFieldStyles}
                        />
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveValidationRule(index)}
                        disabled={validationRules.length === 1}
                        size="small"
                        sx={{
                          color: validationRules.length === 1 ? '#ccc' : '#d32f2f',
                          '&:hover': {
                            backgroundColor: validationRules.length === 1 ? 'transparent' : 'rgba(211, 47, 47, 0.08)'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    onClick={handleAddValidationRule}
                    startIcon={<AddIcon />}
                    variant="outlined"
                    sx={{
                      color: '#475969',
                      borderColor: '#4759694d',
                      borderRadius: '5px',
                      textTransform: 'none',
                      fontSize: '13px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#475969',
                        backgroundColor: 'rgba(71, 89, 105, 0.04)'
                      }
                    }}
                  >
                    Add Another Rule
                  </Button>
                </Box>
              )}
            </Box>

            <Box sx={{
              py: 2,
              backgroundColor: '#ffffff',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3
            }}>
              <Button
                onClick={handleClose}
                disabled={isSaving || isAnalyzing}
                variant="outlined"
                sx={{
                  color: '#475969',
                  borderColor: '#4759694d',
                  borderRadius: '5px',
                  px: 5,
                  py: 0.5,
                  minWidth: '120px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: '#475969',
                    backgroundColor: 'rgba(71, 89, 105, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={isSaving || isAnalyzing}
                sx={{
                  bgcolor: '#485869',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#3a4a59',
                  },
                  borderRadius: '5px',
                  px: 5,
                  py: 0.5,
                  minWidth: '120px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  '&:disabled': {
                    bgcolor: '#cccccc',
                    color: 'rgba(0, 0, 0, 0.26)'
                  }
                }}
              >
                {isSaving ? 'Saving...' : `Save`}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddSectionModal;